import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { FormItem } from '../../components/FormItem';
import { EMPTY_PLACEHOLDER } from '../../constants';
import { useFormContext } from '../../context/FormsContext';
import styles from './Hearings.module.css';

export const courtClerkSchema = z.object({
    firstName: z.string().optional().nullable(),
    lastName: z.string().optional().nullable(),
});

export type CourtClerkType = z.infer<typeof courtClerkSchema>;

interface CourtClerkProps {
    initialValues?: CourtClerkType | null;
    onCourtClerk: (value?: CourtClerkType) => void;
    editMode: boolean;
    hidden?: boolean;
    hearingHappened?: boolean;
}

export const CourtClerk = ({ initialValues, onCourtClerk, editMode, hidden, hearingHappened }: CourtClerkProps) => {
    const { onSubmitListener } = useFormContext();
    const {
        control,
        formState: { errors },
        watch,
        handleSubmit,
        setValue,
        reset,
    } = useForm<CourtClerkType>({
        resolver: zodResolver(courtClerkSchema),
        defaultValues: { firstName: null, lastName: null, ...initialValues },
    });

    useEffect(() => {
        reset({ firstName: null, lastName: null, ...initialValues });
    }, [initialValues]);

    useEffect(() => {
        return onSubmitListener('hearings', () => {
            return new Promise((resolve) => {
                const validateFunc = () => {
                    resolve({ type: 'validate', result: true });
                };
                handleSubmit(validateFunc, validateFunc)();
            });
        });
    }, []);

    useEffect(() => {
        if (!hearingHappened) {
            setValue('firstName', undefined);
            setValue('lastName', undefined);
        }
    }, [hearingHappened, setValue]);

    useEffect(() => {
        const { unsubscribe } = watch((data) => {
            const result = courtClerkSchema.safeParse(data);

            result.success ? onCourtClerk(result.data) : onCourtClerk();
        });

        return unsubscribe;
    }, []);

    if (hidden) {
        return null;
    }

    return (
        <>
            <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                    <FormItem
                        errorMessage={errors.firstName?.message}
                        className={styles.row}
                        editMode={editMode}
                        label="Court Clerk First Name"
                        value={field.value}
                        renderValue={(value) => value || EMPTY_PLACEHOLDER}
                        onChange={(value) => {
                            field.onChange({
                                target: { value },
                            });
                        }}
                    />
                )}
            />
            <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                    <FormItem
                        errorMessage={errors.lastName?.message}
                        className={styles.row}
                        editMode={editMode}
                        label="Court Clerk Last Name"
                        value={field.value}
                        renderValue={(value) => value || EMPTY_PLACEHOLDER}
                        onChange={(value) => {
                            field.onChange({
                                target: { value },
                            });
                        }}
                    />
                )}
            />
        </>
    );
};
